<template>
  <div class="flex w-screen h-screen overflow-hidden">
    <div class="pref bg-purple-600 w-1/2 overflow-hidden">
      <app-logo invert />

      <h2 class="unicorn-h2 text-white mt-8">Здравствуйте!</h2>

      <p class="mt-4 text-lg text-purple-300">
        Личный кабинет <span class="text-white font-bold">я.учитель</span> —
        центральный хаб управления питанием учеников, анализа посещения и
        температурного мониторинга
      </p>

      <div class="preview">
        <img src="../shared/assets/images/figures-2.png" alt="" />
      </div>
    </div>

    <div class="w-3/5 flex p-4 fd">
      <div class="m-auto max-w-sm w-full">
        <u-hide-at breakpoint="lg">
          <app-logo class="mb-8" />
        </u-hide-at>

        <h4 class="unicorn-h4">Вход в личный кабинет</h4>
        <h1 class="unicorn-h1 mb-12">я.учитель</h1>

        <template v-if="isExternalSite">
          <form class="flex flex-col" @submit.prevent="authWithToken($event)">
            <u-text-field
              :value="token.trim().toUpperCase()"
              @input="token = $event.trim().toUpperCase()"
              label="Введите токен авторизации"
            />

            <u-button role="submit" class="mt-4" variant="primary" block>
              Войти
            </u-button>
          </form>

          <u-divider style="margin: 2rem 0"> или </u-divider>

          <qr-code-scanner @qrcode="authWithQrCode" />
        </template>

        <template v-else>
          <form class="flex flex-col" @submit.prevent="auth">
            <u-text-field v-model="username" label="Логин, имя пользователя" />

            <u-text-field
              class="mt-4"
              type="password"
              v-model="password"
              label="Пароль"
            />

            <u-button class="mt-4" variant="primary" block> Войти </u-button>
          </form>
        </template>

        <u-alert v-if="status.name === 'error'" variant="danger" class="mt-4">
          {{ statusText }}
        </u-alert>

        <u-alert
          v-if="connectionNotice"
          :variant="connectionNotice.variant"
          class="mt-4"
        >
          <template #title>{{ connectionNotice.title }}</template>
          {{ connectionNotice.text }}
        </u-alert>

        <mip-logo class="mt-12" />

        <current-version class="mt-4 text-center" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import Api from "../services/Api";
import { saveSession } from "../services/session";
import AppLogo from "@/app/shared/new-component/AppLogo";
import CurrentVersion from "@/app/shared/components/CurrentVersion";
import MipLogo from "@/app/shared/new-component/MipLogo";
import QrCodeScanner from "@/app/shared/new-component/QrCodeScanner";
import { isExternalSite } from "../services/environment";

export default {
  components: { QrCodeScanner, MipLogo, AppLogo, CurrentVersion },

  data() {
    return {
      username: "",
      password: "",
      token: "",
      status: {
        name: "success",
        data: null
      }
    };
  },

  computed: {
    ...mapGetters("connection", ["isDown", "bannerInfo"]),

    isExternalSite() {
      return isExternalSite();
    },

    statusText() {
      return this.status.data || "Ошибка авторизации. Попробуйте позднее";
    },

    /**
     * На странице входа рабочий экран не блокируется, но причину отказа видно
     * сразу: если связи со школой нет, код проверить не получится.
     */
    connectionNotice() {
      if (this.status.name === "error" || !this.isDown) return null;

      return this.bannerInfo;
    }
  },

  async mounted() {
    const token = this.$route.query.token;

    // Ссылка оператора всегда содержит код, но код одноразовый и живёт минуты.
    // Поэтому сначала проверяем, не поднята ли уже сессия: если жива — код не
    // гасим (иначе он «протухает» и учитель принимает это за разлогин), а
    // просто уходим в кабинет.
    if (token) {
      const hasSession = await this.checkActiveSession();

      if (hasSession) {
        // Сессия жива (в том числе поднята restore-токеном, пока cookie была
        // потеряна): переход в кабинет сам убирает код из адресной строки.
        // Отдельный replace на `login` здесь только гонял бы навигацию зря —
        // App.vue в это же время уводит в кабинет своим `checkSession`.
        await this.goHome();
        return;
      }

      this.clearTokenFromUrl();

      // Пока шла проверка, учитель мог отправить код вручную — тогда не дублируем.
      if (this.status.name === "pending") return;

      this.authWithToken(null, token);
      return;
    }

    // Пришли на форму входа с живой сессией (закладка на `#/login`) — уходим
    // в кабинет, не показывая форму.
    if (await this.checkActiveSession()) await this.goHome();
  },

  methods: {
    async auth() {
      const { username, password } = this;

      if (!(username.trim() && password.trim())) return;

      try {
        this.setStatus("pending");
        const { data: profile } = await Api.authorization.login(
          username.trim(),
          password.trim()
        );
        this.setStatus("success");

        saveSession(profile);
        await this.$router.push({ name: "home" });
      } catch (error) {
        this.setStatus("error", this.describeLoginError(error, "password"));
        this.clearTokenFromUrl();
      }
    },

    authWithQrCode(qrcode) {
      const token = qrcode.toString().split("?token=")[1];
      this.authWithToken(null, token);
    },

    async authWithToken(e, token) {
      try {
        this.setStatus("pending");
        const { data: profile } = await Api.authorization.loginWithToken(
          token || this.token.trim()
        );
        this.setStatus("success");

        // Ответ внешнего входа содержит restore_token: saveSession кладёт его в
        // localStorage — им SPA поднимет сессию, если cookie потеряется.
        saveSession(profile);
        await this.$router.push({ name: "home" });
      } catch (error) {
        this.setStatus("error", this.describeLoginError(error, "token"));
        this.clearTokenFromUrl();
      }
    },

    /**
     * Причина отказа показывается в обеих ветках формы: раньше сообщение жило
     * только в ветке логина/пароля, и на внешней площадке код «молчал».
     *
     * `mode` различает ветки — пароль и одноразовый код. Прямой сервер школы
     * (little, Spring Security) на неверный пароль отдаёт техническое message
     * из HTTP-статуса («Unauthorized»), а не доменный текст, как облако,
     * поэтому такие сообщения не показываем и подставляем свой текст.
     */
    describeLoginError(error, mode = "token") {
      const { kind, status } = Api.describeError(error);
      const payload = error && error.response && error.response.data;
      const raw = payload && typeof payload === "object" ? payload.message : "";
      const technical =
        typeof raw === "string" &&
        /^(unauthorized|forbidden|bad request|not found)$/i.test(raw.trim());

      if (raw && !technical) return raw;

      if (kind === "school_down") {
        return "Нет связи со школьным сервером — код не проверен. Сгенерируйте новый код и повторите позже.";
      }

      if (kind === "cloud_down" || kind === "offline") {
        return "Сервис недоступен. Проверьте подключение к сети и повторите попытку.";
      }

      if (mode === "password" && (status === 400 || status === 401)) {
        return "Неверный логин или пароль";
      }

      if (kind === "domain" || kind === "session") {
        return "Код не найден, уже использован или просрочен. Сгенерируйте новый код.";
      }

      return "Ошибка авторизации. Попробуйте позднее";
    },

    /** Код одноразовый: убираем его из адресной строки, чтобы F5 не отправил его повторно. */
    clearTokenFromUrl() {
      if (!this.$route.query.token) return;

      this.$router.replace({ name: "login" }).catch(() => {});
    },

    /**
     * Проверка уже поднятой сессии. Раньше форма входа слепо гасила код из
     * ссылки, даже когда учитель был авторизован: код одноразовый, повторно
     * использовать его нельзя, и учитель видел «код просрочен» вместо кабинета.
     */
    async checkActiveSession() {
      try {
        const { data: profile } = await Api.authorization.currentUser();
        saveSession(profile);
        this.$store.commit("setExplicitLogout", false);
        return true;
      } catch (error) {
        return false;
      }
    },

    async goHome() {
      try {
        await this.$router.replace({ name: "home" });
      } catch (error) {
        // Навигация может быть прервана — тогда остаёмся на текущем экране.
      }
    },

    setStatus(name, data) {
      this.status = { name, data };
    }
  }
};
</script>

<style scoped>
.preview {
  position: fixed;
  top: 270px;
  left: -370px;
  width: 1380px;
  pointer-events: none;
}

.pref {
  @apply p-20;
}

@media screen and (max-width: 1200px) {
  .preview {
    left: -50%;
  }

  .pref {
    @apply p-10;
  }
}

@media screen and (max-width: 1024px) {
  .pref {
    @apply hidden;
  }

  .fd {
    @apply w-full;
  }
}
</style>
